import { DOCUMENT, NgClass } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  Signal,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MediaWatcherService } from '@iot-platform/core';
import { ResizeHandleConfig, ResizeHandleService } from './resize-handle.service';
import { ResizeStorageService } from './resize-storage.service';

@Component({
  imports: [MatIcon, NgClass],
  selector: 'iot-platform-ui-resize-handle',
  templateUrl: './resize-handle.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./resize-handle.component.scss']
})
export class ResizeHandleComponent implements OnInit, AfterViewInit, OnDestroy {
  // Inputs
  pageName = input<string>();
  minWidth = input.required<number | string>();
  maxWidth = input.required<number | string>();
  resizable = input<boolean>();
  position = input.required<'start' | 'end'>();
  resizeElementOpened = input.required<boolean>();
  mainSideBarMenuOpened = input<boolean>();
  defaultWidth = input<number | string>();
  parentContainerSelector = input<string>();
  // Outputs
  contentWidth = output<string>();
  width = output<string>();
  private readonly document = inject(DOCUMENT);
  private readonly resizeService = inject(ResizeHandleService);
  private readonly mediaWatcherService = inject(MediaWatcherService);

  // Computed values
  readonly isXSmallScreen = this.mediaWatcherService.isXSmallScreen;
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly storageService = inject(ResizeStorageService);
  // Internal state
  private readonly elementWidth = signal<string>('25rem');
  private readonly isResizing = signal<boolean>(false);
  private resizeListeners: any = null;
  private readonly elementMinWidth = computed(() => {
    const isXSmallScreen = this.isXSmallScreen();
    const minWidth = this.minWidth();
    return isXSmallScreen ? 250 : minWidth;
  });
  private readonly elementMaxWidth = computed(() => {
    const isXSmallScreen = this.isXSmallScreen();
    const maxWidth = this.maxWidth();
    return isXSmallScreen ? '90%' : maxWidth;
  });
  private readonly config: Signal<ResizeHandleConfig> = computed(
    () =>
      ({
        minWidth: this.elementMinWidth(),
        maxWidth: this.elementMaxWidth(),
        position: this.position(),
        resizable: this.resizable(),
        isMobile: this.isXSmallScreen()
      }) as ResizeHandleConfig
  );

  private readonly contentWidthValue = computed(() => {
    const opened = this.resizeElementOpened();
    const resizable = this.resizable();
    const elementWidth = this.elementWidth();
    const mainSideBarMenuOpened = this.mainSideBarMenuOpened();

    if (resizable && opened) {
      const sidebarOffset = mainSideBarMenuOpened ? '250px' : '0px';
      return `calc(100% - ${sidebarOffset} - ${elementWidth})`;
    }
    return 'auto';
  });

  constructor() {
    effect(() => {
      this.contentWidth.emit(this.contentWidthValue());
    });

    effect(() => {
      this.width.emit(this.elementWidth());
    });
  }

  ngOnInit(): void {
    this.initializeWidth();
  }

  ngAfterViewInit(): void {
    this.resizeService.setContainer(this.elementRef?.nativeElement);
  }

  ngOnDestroy(): void {
    this.cleanupResizeListeners();
  }

  onResizeStart(event: MouseEvent | TouchEvent): void {
    const currentWidth = this.getSavedWidth() ?? this.defaultWidth() ?? this.resizeService.getCurrentWidth();
    if (!this.resizable() || !this.resizeService.startResize(event, this.config(), currentWidth as string)) {
      return;
    }

    this.isResizing.set(true);
    this.elementWidth.set(currentWidth as string);
    this.setupResizeListeners();
  }

  private getParentContainer() {
    const selector = this.parentContainerSelector();
    if (!selector) {
      return this.document;
    }

    let element: HTMLElement | null = this.elementRef.nativeElement;
    while (element) {
      if (element.matches(selector)) {
        return element;
      }
      element = element.parentElement;
    }

    return this.document;
  }

  private setupResizeListeners(): void {
    const container = this.getParentContainer();

    const onMove = (event: MouseEvent | TouchEvent) => {
      const newWidth = this.resizeService.moveResize(event, this.config());
      if (newWidth) {
        this.elementWidth.set(newWidth);
      }
    };

    const onEnd = (event: MouseEvent | TouchEvent) => {
      this.resizeService.endResize(event);
      this.saveWidth(this.elementWidth());
      this.cleanupResizeListeners();
      this.isResizing.set(false);
    };

    container.addEventListener('mousemove', onMove as never);
    container.addEventListener('mouseup', onEnd as never);
    container.addEventListener('touchmove', onMove as never, { passive: false });
    container.addEventListener('touchend', onEnd as never);

    this.resizeListeners = { onMove, onEnd, container };
  }

  private cleanupResizeListeners(): void {
    if (!this.resizeListeners) return;

    const { onMove, onEnd, container } = this.resizeListeners;
    container.removeEventListener('mousemove', onMove);
    container.removeEventListener('mouseup', onEnd);
    container.removeEventListener('touchmove', onMove);
    container.removeEventListener('touchend', onEnd);

    this.resizeListeners = null;
  }

  private initializeWidth(): void {
    const isXSmallScreen = this.isXSmallScreen();
    const defaultWidth = this.defaultWidth();
    const savedWidth = this.getSavedWidth();

    if (isXSmallScreen) {
      this.resizeService.initializeWidth('70%');
      this.elementWidth.set('70%');
    } else if (savedWidth) {
      this.resizeService.initializeWidth(savedWidth);
      this.elementWidth.set(savedWidth);
    } else if (defaultWidth) {
      this.resizeService.initializeWidth(defaultWidth as string);
      this.elementWidth.set(defaultWidth as string);
    }
  }

  private getSavedWidth(): string | null {
    const pageName = this.pageName();
    return pageName ? this.storageService.getWidth(pageName) : null;
  }

  private saveWidth(width: string): void {
    const pageName = this.pageName();
    if (pageName) {
      this.storageService.setWidth(pageName, width);
    }
  }
}
